<template>
    <div class="file-input-wrapper">
        <div
            class="file-input-area"
            :class="{ 
                'dragover': isDragOver,
                'has-file': hasFile,
                'is-image': isImage && hasFile 
            }"
            @click="triggerFileInput"
            @drop="onDrop"
            @dragover.prevent="onDragOver"
            @dragleave="onDragLeave"
            @dragenter.prevent
        >
            <!-- Icon và content khi chưa có file -->
            <div v-if="!hasFile" class="upload-content">
                <v-icon size="48" color="primary" class="upload-icon">{{ uploadIcon }}</v-icon>
                <h4 class="upload-title">{{ title }}</h4>
                <p class="upload-subtitle">{{ subtitle }}</p>
                <v-btn color="primary" outlined small>
                    <v-icon left small>mdi-file-upload</v-icon>
                    Chọn file
                </v-btn>
            </div>

            <!-- Preview khi đã có file -->
            <div v-else class="file-preview">
                <!-- Image preview -->
                <div v-if="isImage" class="image-preview">
                    <img :src="previewUrl" :alt="fileName" class="preview-image" />
                    <div class="image-overlay">
                        <v-btn icon small color="white" @click.stop="removeFile">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>

                <!-- File info for non-images -->
                <div v-else class="file-info">
                    <div class="file-icon-container">
                        <v-icon size="40" :color="getFileColor(fileType)">{{ getFileIcon(fileType) }}</v-icon>
                    </div>
                    <div class="file-details">
                        <h4 class="file-name">{{ fileName }}</h4>
                        <p class="file-size">{{ formatFileSize(fileSize) }}</p>
                        <p class="file-type">{{ fileType.toUpperCase() }}</p>
                        <p v-if="currentFile && currentFile.isExisting" class="file-status">
                            <v-icon small color="success">mdi-check-circle</v-icon>
                            Existing file
                        </p>
                    </div>
                    <v-btn icon small color="error" @click.stop="removeFile" class="remove-btn">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>

            <!-- Drag overlay -->
            <div v-if="isDragOver" class="drag-overlay">
                <v-icon size="64" color="primary">mdi-file-upload</v-icon>
                <h3>Thả file vào đây</h3>
            </div>
        </div>

        <!-- Hidden file input -->
        <input
            ref="fileInput"
            type="file"
            :accept="accept"
            @change="onFileChange"
            style="display: none"
        />

        <!-- Error message -->
        <div v-if="errorMessage" class="error-message">
            <v-icon small color="error">mdi-alert-circle</v-icon>
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'FileInput',
    props: {
        accept: {
            type: String,
            default: '*'
        },
        title: {
            type: String,
            default: 'Tải file lên'
        },
        subtitle: {
            type: String,
            default: 'Kéo thả file hoặc click để chọn'
        },
        maxSize: {
            type: Number,
            default: 10 * 1024 * 1024 // 10MB
        },
        modelValue: {
            type: [String, Object],
            default: null
        }
    },
    data() {
        return {
            isDragOver: false,
            currentFile: null,
            previewUrl: null,
            errorMessage: null,
            isEmittingFromComponent: false
        };
    },
    computed: {
        hasFile() {
            return this.currentFile !== null;
        },
        isImage() {
            return this.currentFile && this.currentFile.type.startsWith('image/');
        },
        fileName() {
            return this.currentFile ? this.currentFile.name : '';
        },
        fileSize() {
            return this.currentFile ? this.currentFile.size : 0;
        },
        fileType() {
            if (!this.currentFile) return '';
            const extension = this.currentFile.name.split('.').pop().toLowerCase();
            return extension;
        },
        uploadIcon() {
            if (this.accept.includes('image')) return 'mdi-image-plus';
            if (this.accept.includes('.zip')) return 'mdi-file-zip';
            return 'mdi-file-upload';
        }
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        onFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.handleFile(file);
            }
        },
        onDrop(event) {
            event.preventDefault();
            this.isDragOver = false;
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.handleFile(files[0]);
            }
        },
        onDragOver(event) {
            event.preventDefault();
            this.isDragOver = true;
        },
        onDragLeave() {
            this.isDragOver = false;
        },
        handleFile(file) {
            this.errorMessage = null;

            // Check file size
            if (file.size > this.maxSize) {
                this.errorMessage = `File quá lớn. Tối đa ${this.formatFileSize(this.maxSize)}`;
                return;
            }

            // Check file type
            if (this.accept !== '*' && !this.isFileTypeAccepted(file)) {
                this.errorMessage = `Loại file không được hỗ trợ. Chỉ chấp nhận: ${this.accept}`;
                return;
            }

            this.currentFile = file;
            this.createPreview(file);
            this.emitFile(file);
        },
        createPreview(file) {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.previewUrl = e.target.result;
                };
                reader.readAsDataURL(file);
            } else {
                // For non-image files, create a base64 representation
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.previewUrl = e.target.result;
                };
                reader.readAsDataURL(file);
            }
        },
        emitFile(file) {
            if (file.type.startsWith('image/')) {
                // For images, emit the data URL
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.isEmittingFromComponent = true;
                    this.$emit('update:modelValue', e.target.result);
                    this.$nextTick(() => {
                        this.isEmittingFromComponent = false;
                    });
                };
                reader.readAsDataURL(file);
            } else {
                // For other files, emit file info object
                const reader = new FileReader();
                reader.onload = (e) => {
                    const fileData = {
                        name: file.name,
                        data: e.target.result,
                        size: file.size,
                        type: file.type
                    };
                    this.isEmittingFromComponent = true;
                    this.$emit('update:modelValue', fileData);
                    this.$nextTick(() => {
                        this.isEmittingFromComponent = false;
                    });
                };
                reader.readAsDataURL(file);
            }
        },
        removeFile() {
            this.currentFile = null;
            this.previewUrl = null;
            this.errorMessage = null;
            this.isEmittingFromComponent = true;
            this.$emit('update:modelValue', null);
            this.$nextTick(() => {
                this.isEmittingFromComponent = false;
            });
            this.$refs.fileInput.value = '';
        },
        isFileTypeAccepted(file) {
            if (this.accept === '*') return true;
            
            const acceptedTypes = this.accept.split(',').map(type => type.trim());
            
            return acceptedTypes.some(type => {
                if (type.startsWith('.')) {
                    return file.name.toLowerCase().endsWith(type.toLowerCase());
                }
                return file.type.match(type);
            });
        },
        formatFileSize(bytes) {
            if (bytes === 'Unknown') return 'Unknown';
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        getFileIcon(type) {
            const icons = {
                'zip': 'mdi-file-zip',
                'pdf': 'mdi-file-pdf-box',
                'doc': 'mdi-file-word',
                'docx': 'mdi-file-word',
                'xls': 'mdi-file-excel',
                'xlsx': 'mdi-file-excel',
                'txt': 'mdi-file-document',
                'csv': 'mdi-file-table'
            };
            return icons[type] || 'mdi-file';
        },
        getFileColor(type) {
            const colors = {
                'zip': 'orange',
                'pdf': 'red',
                'doc': 'blue',
                'docx': 'blue',
                'xls': 'green',
                'xlsx': 'green',
                'txt': 'grey',
                'csv': 'green'
            };
            return colors[type] || 'primary';
        },
        loadExistingFile(url) {
            // For image URLs
            this.previewUrl = url;
            // Create a mock file object for display
            const fileName = url.split('/').pop() || 'Existing file';
            
            // Better file type detection
            const getFileType = (filename) => {
                const ext = filename.split('.').pop().toLowerCase();
                const mimeTypes = {
                    'jpg': 'image/jpeg',
                    'jpeg': 'image/jpeg', 
                    'png': 'image/png',
                    'gif': 'image/gif',
                    'webp': 'image/webp',
                    'svg': 'image/svg+xml',
                    'bmp': 'image/bmp',
                    'zip': 'application/zip',
                    'pdf': 'application/pdf',
                    'doc': 'application/msword',
                    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                };
                return mimeTypes[ext] || 'application/octet-stream';
            };
            
            const mockFile = {
                name: fileName,
                type: getFileType(fileName),
                size: 'Unknown', // Show as "Unknown" instead of 0
                isExisting: true,
                url: url
            };
            this.currentFile = mockFile;
            
            // Try to fetch actual file size for images
            if (mockFile.type.startsWith('image/')) {
                this.fetchFileSize(url);
            }
        },
        
        async fetchFileSize(url) {
            try {
                const response = await fetch(url, { method: 'HEAD' });
                const size = response.headers.get('content-length');
                if (size && this.currentFile && this.currentFile.isExisting) {
                    this.currentFile.size = parseInt(size);
                }
            } catch (error) {
                // Ignore errors - keep as "Unknown"
                console.log('Could not fetch file size:', error);
            }
        },
        loadExistingFileObject(fileObj) {
            // For file objects with metadata
            if (fileObj.url) {
                this.previewUrl = fileObj.url;
                const mockFile = {
                    name: fileObj.name || 'Existing file',
                    type: fileObj.type || 'application/octet-stream',
                    size: fileObj.size || 'Unknown',
                    isExisting: true,
                    url: fileObj.url
                };
                this.currentFile = mockFile;
            }
        }
    },
    watch: {
        modelValue(newValue, oldValue) {
            // Skip watcher logic if we're emitting from this component
            if (this.isEmittingFromComponent) {
                return;
            }
            
            if (!newValue) {
                // Clear local state without emitting to avoid infinite loop
                this.currentFile = null;
                this.previewUrl = null;
                this.errorMessage = null;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
            } else if (typeof newValue === 'string' && newValue.trim() !== '') {
                // Handle existing image URL
                this.loadExistingFile(newValue);
            } else if (typeof newValue === 'object' && newValue !== null) {
                // Handle existing file object
                this.loadExistingFileObject(newValue);
            }
        }
    },
    mounted() {
        // Load existing data on mount
        if (this.modelValue) {
            if (typeof this.modelValue === 'string' && this.modelValue.trim() !== '') {
                this.loadExistingFile(this.modelValue);
            } else if (typeof this.modelValue === 'object' && this.modelValue !== null) {
                this.loadExistingFileObject(this.modelValue);
            }
        }
    }
};
</script>

<style scoped>
.file-input-wrapper {
    width: 100%;
}

.file-input-area {
    position: relative;
    border: 2px dashed #ddd;
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #fafafa;
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.file-input-area:hover {
    border-color: #1976d2;
    background-color: #f3f8ff;
}

.file-input-area.dragover {
    border-color: #1976d2;
    background-color: #e3f2fd;
    transform: scale(1.02);
}

.file-input-area.has-file {
    border-color: #4caf50;
    background-color: #f8fff8;
}

.file-input-area.is-image {
    padding: 8px;
    min-height: 200px;
}

.upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.upload-icon {
    opacity: 0.6;
    margin-bottom: 8px;
}

.upload-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.upload-subtitle {
    margin: 0;
    color: #666;
    font-size: 14px;
}

/* File Preview */
.file-preview {
    width: 100%;
    height: 100%;
}

.image-preview {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 180px;
    border-radius: 6px;
    overflow: hidden;
}

.preview-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 6px;
}

.image-overlay {
    position: absolute;
    top: 8px;
    right: 8px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.image-preview:hover .image-overlay {
    opacity: 1;
}

.file-info {
    display: flex;
    align-items: center;
    gap: 16px;
    text-align: left;
}

.file-icon-container {
    flex-shrink: 0;
}

.file-details {
    flex: 1;
}

.file-name {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    word-break: break-word;
}

.file-size {
    margin: 0 0 4px 0;
    font-size: 14px;
    color: #666;
}

.file-type {
    margin: 0;
    font-size: 12px;
    color: #999;
    font-weight: 600;
}

.file-status {
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #4caf50;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
}

.remove-btn {
    flex-shrink: 0;
}

/* Drag Overlay */
.drag-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(25, 118, 210, 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border-radius: 6px;
    backdrop-filter: blur(2px);
}

.drag-overlay h3 {
    margin: 0;
    color: #1976d2;
    font-weight: 600;
}

/* Error Message */
.error-message {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 12px;
    background-color: #ffebee;
    border: 1px solid #f44336;
    border-radius: 4px;
    color: #c62828;
    font-size: 14px;
}

/* Responsive */
@media (max-width: 600px) {
    .file-input-area {
        padding: 16px;
        min-height: 140px;
    }
    
    .upload-title {
        font-size: 16px;
    }
    
    .upload-subtitle {
        font-size: 13px;
    }
    
    .file-info {
        flex-direction: column;
        text-align: center;
        gap: 12px;
    }
    
    .file-details {
        order: 2;
    }
    
    .remove-btn {
        order: 3;
    }
}
</style> 